import React, { useCallback, useEffect, useState } from "react";

import MetricBox from "../MetricBox";

import styles from "./DashboardTab.module.scss";
import TimeBarGraph from "../DayBarGraph";
import ProjectDashboardService from "api/Project/ProjectDashboardService";

interface DashboardTabProps {
  proj_id: string;
}

const DashboardTab = ({ proj_id }: DashboardTabProps) => {
  const [startDateDaily, setStartDateDaily] = useState<Date>(new Date());
  const [startDateHourly, setStartDateHourly] = useState<Date>(new Date());

  const [totalRequestsDay, setTotalRequestsDay] = useState<number>(0);
  const [totalRequestsHour, setTotalRequestsHour] = useState<number>(0);
  const [requestsDailyData, setRequestsDailyData] = useState<number[]>([]);
  const [requestsHourlyData, setRequestsHourlyData] = useState<number[]>([]);

  const [totalCreditsSpentDay, setTotalCreditsSpentDay] = useState<number>(0);
  const [totalCreditsSpentHour, setTotalCreditsSpentHour] = useState<number>(0);
  const [creditsSpentDailyData, setCreditsSpentDailyData] = useState<number[]>(
    [],
  );
  const [creditsSpentHourlyData, setCreditsSpentHourlyData] = useState<
    number[]
  >([]);

  const [latencyDailyData, setLatencyDailyData] = useState<number[]>([]);

  const [moneySavedHourlyDaily] = useState<number[]>([
    21, 19, 32, 22, 36, 31, 30,
  ]);

  const getProjectDashboardData = useCallback(async () => {
    try {
      const response =
        await ProjectDashboardService.getProjectDashboardData(proj_id);

      setTotalRequestsDay(response.total_requests_past_day);
      setTotalRequestsHour(response.total_requests_curr_hour);
      setRequestsHourlyData(response.hourly_requests);
      setRequestsDailyData(response.daily_requests);

      setTotalCreditsSpentDay(response.total_credits_spent_past_day);
      setTotalCreditsSpentHour(response.total_credits_spent_curr_hour);
      setCreditsSpentHourlyData(response.hourly_credits_spent);
      setCreditsSpentDailyData(response.daily_credits_spent);

      setLatencyDailyData(response.daily_average_latency);

      setStartDateHourly(new Date(response.start_date_hourly));
      setStartDateDaily(new Date(response.start_date_daily));
    } catch (err) {
      // handle error
    }
  }, [proj_id]);

  useEffect(() => {
    getProjectDashboardData();
  }, [getProjectDashboardData]);

  return (
    <div className={styles.content}>
      <div className={styles.centerContents}>
        <div className={styles.metricsRow}>
          <MetricBox
            title="Today Requests"
            value={`${totalRequestsDay}`}
            subtitle={`${totalRequestsHour} in this hour`}
            graphData={requestsHourlyData}
            startDate={startDateHourly}
            getTooltipLabel={(index) => `${requestsHourlyData[index]} requests`}
          />
          <div className={styles.separator} />
          <MetricBox
            title="Today Credits Spent"
            value={`${totalCreditsSpentDay}`}
            subtitle={`${totalCreditsSpentHour} in this hour`}
            graphData={creditsSpentHourlyData}
            startDate={startDateHourly}
            getTooltipLabel={(index) =>
              `${creditsSpentHourlyData[index]} credits`
            }
          />
          <div className={styles.separator} />
          <MetricBox
            title="Average Money Saved"
            value="30%"
            subtitle="25% in the past hour"
            graphData={moneySavedHourlyDaily}
            startDate={startDateHourly}
            getTooltipLabel={(index) =>
              `${moneySavedHourlyDaily[index]}% saved`
            }
          />
        </div>
      </div>

      <div className={styles.graphSection}>
        <span className={styles.title}>Monthly Requests</span>
        <div className={styles.chart}>
          <TimeBarGraph
            values={requestsDailyData}
            startDate={startDateDaily}
            getTooltipLabel={(index) => `${requestsDailyData[index]} requests`}
          />
        </div>
      </div>

      <div className={styles.graphSection}>
        <span className={styles.title}>Credits Spent</span>
        <div className={styles.chart}>
          <TimeBarGraph
            values={creditsSpentDailyData}
            startDate={startDateDaily}
            getTooltipLabel={(index) =>
              `${creditsSpentDailyData[index]} credits`
            }
          />
        </div>
      </div>

      <div className={styles.graphSection}>
        <span className={styles.title}>Request Latency</span>
        <div className={styles.chart}>
          <TimeBarGraph
            values={latencyDailyData}
            startDate={startDateDaily}
            getTooltipLabel={(index) => `${latencyDailyData[index]} ms`}
          />
        </div>
      </div>
    </div>
  );
};

export default DashboardTab;
